// ==========================================
// Email Capture Service
// Rate limit checking for UI display (actual enforcement in /api/journey)
// localStorage tracking for session persistence
// ==========================================

import { USE_MOCK } from '@/lib/mocks/mockJourneyResponse';

// API endpoint for rate limit checking (read-only)
const CHECK_LIMIT_ENDPOINT = '/api/journey/check-limit';

// localStorage keys
const STORAGE_KEYS = {
  EMAILS: 'nri_fire_captured_emails',
  SESSION_EMAIL: 'nri_fire_session_email',
  DEVICE_ID: 'nri_fire_device_id',
} as const

/**
 * Generate or retrieve a unique device ID
 * Used for rate limiting across email changes
 */
export function getOrCreateDeviceId(): string {
  try {
    let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID)
    if (!deviceId) {
      // Generate a unique ID using crypto API
      deviceId = crypto.randomUUID()
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId)
    }
    return deviceId
  } catch {
    // Fallback for environments without crypto or localStorage
    return 'unknown-device'
  }
}

interface CapturedEmail {
  email: string
  source: 'journey_generation'
  timestamp: number
}

interface RateLimitResponse {
  canGenerate: boolean
  remaining: number
  dailyRemaining: number
  weeklyRemaining: number
  dailyLimit: number
  weeklyLimit: number
  resetDate: string | null
  blockReason?: 'email' | 'ip' | 'device'
  blockWindow?: 'daily' | 'weekly'
}

/**
 * Check rate limit for an email via Vercel KV API (read-only for UI display)
 * Note: Actual enforcement happens in /api/journey
 */
export async function checkRateLimit(email: string): Promise<RateLimitResponse> {
  // Default response (fail-open for UI)
  const defaultResponse: RateLimitResponse = {
    canGenerate: true,
    remaining: 2,
    dailyRemaining: 2,
    weeklyRemaining: 3,
    dailyLimit: 2,
    weeklyLimit: 3,
    resetDate: null,
  };

  // Mock mode - skip API call
  if (USE_MOCK) {
    return defaultResponse;
  }

  try {
    const deviceId = getOrCreateDeviceId();
    const response = await fetch(
      `${CHECK_LIMIT_ENDPOINT}?email=${encodeURIComponent(email.toLowerCase().trim())}&deviceId=${encodeURIComponent(deviceId)}`
    );

    if (!response.ok) {
      console.error('Rate limit check failed:', response.status);
      // Fail open for UI - server will enforce anyway
      return defaultResponse;
    }

    return await response.json();
  } catch (error) {
    console.error('Rate limit check error:', error);
    // Fail open for UI - server will enforce anyway
    return defaultResponse;
  }
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

/**
 * Save captured email to localStorage (backup)
 */
export function saveEmailToStorage(email: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.EMAILS)
    const emails: CapturedEmail[] = stored ? JSON.parse(stored) : []

    // Check for duplicate
    if (!emails.some(e => e.email.toLowerCase() === email.toLowerCase())) {
      emails.push({
        email: email.trim().toLowerCase(),
        source: 'journey_generation',
        timestamp: Date.now(),
      })
      localStorage.setItem(STORAGE_KEYS.EMAILS, JSON.stringify(emails))
    }

    // Also store as session email for pre-fill
    sessionStorage.setItem(STORAGE_KEYS.SESSION_EMAIL, email.trim().toLowerCase())
  } catch (error) {
    console.error('Failed to save email to storage:', error)
  }
}

/**
 * Get session email for pre-filling forms
 */
export function getSessionEmail(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.SESSION_EMAIL)
  } catch {
    return null
  }
}

/**
 * Check if email is already captured in this session
 */
export function isEmailCaptured(): boolean {
  return !!getSessionEmail()
}
